import { AddCircle, CloseCircle } from "iconsax-react-nativejs";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Keyboard,
  LayoutChangeEvent,
  Modal,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { SelectableItem } from "./item-row";
import ScButton from "../../ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import ItemSelectorList from "./item-selector-list";

type ItemSelectorModalProps = Readonly<{
  items?: SelectableItem[];
  selectedItems?: SelectableItem[];
  visible: boolean;
  onClose: () => void;
  onConfirm?: (ids: string[], items: SelectableItem[]) => void;
  titleKey?: string;
  confirmButtonKey?: string;
  searchPlaceholderKey?: string;
  filterFn?: (item: SelectableItem, search: string) => boolean;
  /** Load the next page when the list is scrolled to the end. */
  onEndReached?: () => void;
  isFetchingMore?: boolean;
  /** Icon on the confirm button. `null` renders it without one. */
  confirmIcon?: React.ReactElement | null;
  /** Shown when there is nothing to list at all — see ItemSelectorList. */
  emptyLabelKey?: string;
  /** Server-side search — see ItemSelectorList. */
  onSearchChange?: (search: string) => void;
  totalCount?: number;
}>;

const defaultFilterFn = (item: SelectableItem, search: string) => {
  const lower = search.toLowerCase();
  return (
    item.displayName?.toLowerCase().includes(lower) ??
    item.userName?.toLowerCase().includes(lower) ??
    false
  );
};

const ItemSelectorModal = ({
  items = [],
  selectedItems = [],
  visible,
  onClose,
  onConfirm,
  titleKey = "LABEL_INVITE_USER",
  confirmButtonKey = "BTN_INVITE",
  searchPlaceholderKey = "LABEL_SEARCH",
  filterFn = defaultFilterFn,
  onEndReached,
  isFetchingMore,
  confirmIcon,
  onSearchChange,
  totalCount,
  emptyLabelKey,
}: ItemSelectorModalProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const [selected, setSelected] = useState<SelectableItem[]>(selectedItems);

  const sheetMaxHeight = windowHeight * 0.8;

  /**
   * The list's `flexGrow`/`minHeight: 0` sizing is the textbook fix for a
   * flex child that needs to shrink to the space left by its siblings, but on
   * web that chain silently broke somewhere between this sheet's `maxHeight`
   * (a percentage, not a definite size) and the FlatList several components
   * down — the list kept its full content height, ran on underneath the
   * Cancel/Add footer, and never became scrollable.
   *
   * Measuring the header and footer and handing ItemSelectorList the real
   * pixel height left over (sheetMaxHeight minus both) sidesteps that chain
   * entirely: a definite `height` on a scroll container always clips and
   * scrolls, on every platform, regardless of how the ancestors resolved
   * their flex sizing. ItemSelectorList further splits this between its own
   * search/count row and the FlatList — see the note there.
   */
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const availableHeight =
    headerHeight > 0 && footerHeight > 0
      ? Math.max(sheetMaxHeight - headerHeight - footerHeight, 120)
      : undefined;

  /**
   * Seed the working selection from the caller's committed one, but only when the
   * modal opens.
   *
   * This used to list `selectedItems` as a dependency, which made the picker
   * impossible to use: callers pass a freshly derived array — the mention call
   * sites don't pass one at all and land on the `[]` default, allocated anew on
   * every render — so the identity changed on each pass. Every toggle therefore
   * re-ran this effect and immediately reset the selection, the checkbox
   * flickered back off, the count stayed at zero, and confirming added nobody.
   *
   * Reading the prop through a ref keeps the "seed on open" behaviour without
   * making a caller's array identity part of the trigger.
   */
  const committedSelection = useRef(selectedItems);
  committedSelection.current = selectedItems;

  useEffect(() => {
    if (visible) {
      setSelected(committedSelection.current);
      // The comment mention picker is opened by typing "@" into the comment
      // box, so it arrives with the keyboard up — which on a phone sits exactly
      // where the confirm/cancel footer is. Nothing in this sheet is typed into
      // except its own search field, so drop the keyboard on open.
      Keyboard.dismiss();
    }
  }, [visible]);

  const selectedIds = selected.map((item) => item.itemId);

  const toggle = (item: SelectableItem) => {
    setSelected((prev) =>
      prev.some((itm) => itm.itemId === item.itemId)
        ? prev.filter((itm) => itm.itemId !== item.itemId)
        : [...prev, item],
    );
  };

  const handleConfirm = () => {
    onConfirm?.(selectedIds, selected);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        className="flex-1 items-center justify-center px-4 bg-black/60"
      >
        {/* Capped, not fixed — the sheet shrinks to fit short lists and caps
            at sheetMaxHeight for long ones (this modal can render inside a
            smaller parent, e.g. the comment mention picker opens nested
            inside the post detail modal). */}
        <View
          style={[
            themeVars as any,
            { maxHeight: sheetMaxHeight, overflow: "hidden" },
          ]}
          className="w-full max-w-md bg-chalk rounded-2xl overflow-hidden"
        >
          {/* Header. Explicit bg + elevation: if the list below ever renders
              taller than its own bounds (a nested-modal layout quirk), this
              still has to visually win, not let list rows show through a
              background it never set for itself. */}
          <View
            onLayout={(e: LayoutChangeEvent) =>
              setHeaderHeight(e.nativeEvent.layout.height)
            }
            style={{ flexShrink: 0, zIndex: 2, elevation: 2 }}
            className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-ash-3 bg-chalk"
          >
            <Text className="text-base text-onyx">{t(titleKey)}</Text>
            <Pressable onPress={onClose} className="p-1.5 rounded-full hover:bg-ash-3">
              <CloseCircle size={20} color="var(--color-ash-1)" />
            </Pressable>
          </View>

          {/* List */}
          <ItemSelectorList
            items={items}
            selectedIds={selectedIds}
            searchPlaceholderKey={searchPlaceholderKey}
            filterFn={filterFn}
            onToggle={toggle}
            onClear={() => setSelected([])}
            onEndReached={onEndReached}
            isFetchingMore={isFetchingMore}
            availableHeight={availableHeight}
            onSearchChange={onSearchChange}
            totalCount={totalCount}
            emptyLabelKey={emptyLabelKey}
          />

          {/* Footer. `flexShrink: 0` so it always reserves its own height —
              the list above shrinks to fit around it, never the other way.
              Same explicit bg + elevation reasoning as the header. */}
          <View
            onLayout={(e: LayoutChangeEvent) =>
              setFooterHeight(e.nativeEvent.layout.height)
            }
            style={{ flexShrink: 0, zIndex: 2, elevation: 2 }}
            className="flex-row items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 bg-chalk"
          >
            <ScButton text="BTN_CANCEL" mode="outlined" onPress={onClose} />
            <ScButton
              text={confirmButtonKey}
              onPress={handleConfirm}
              icon={
                confirmIcon === undefined ? (
                  <AddCircle size={18} color="white" />
                ) : (
                  confirmIcon
                )
              }
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ItemSelectorModal;
